import { getCanonicalPageUrl } from '@findy-code/utils/next-js';
import { runWithSentry } from '@findy-code/utils/sentry';
import type { FetchCompanyDetailQuery } from '@findy-code/yuffie/feature-company-detail';
import {
  CompanyDetailContainer,
  FetchCompanyDetailDocument,
} from '@findy-code/yuffie/feature-company-detail';
import { LayoutContainer } from '@findy-code/yuffie/feature-layout';
import { createSsrClient } from '@findy-code/yuffie/graphql';
import { generateTitle } from '@findy-code/yuffie/utils/title';
import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next';
import Head from 'next/head';

export const CompaniesShowPage = ({
  isLogin,
  isLoadingUserData,
  companyName = null,
  url,
  ogpImage = null,
  company,
  notFound,
}: Props) => {
  const pageTitle = generateTitle(
    typeof companyName === 'string' ? `${companyName}` : undefined
  );

  return (
    <>
      <Head>
        <title data-testid="meta-title">{pageTitle}</title>
      </Head>
      <Head>
        {typeof companyName === 'string' && (
          <>
            <meta
              content={pageTitle}
              data-testid="meta-og-title"
              key="og:title"
              property="og:title"
            />
            <meta content={pageTitle} name="twitter:title" />
          </>
        )}

        <meta content={url} key="og:url" property="og:url" />
        <link href={url} rel="canonical" />

        <meta content="article" key="og:type" property="og:type" />

        {typeof ogpImage === 'string' && (
          <>
            <meta content={ogpImage} key="og:image" property="og:image" />
            <meta content={ogpImage} name="twitter:image" />
            <meta content={ogpImage} itemProp="image" />
          </>
        )}
        {notFound && <meta content="noindex,nofollow" name="robots" />}
      </Head>
      <LayoutContainer
        isShowLoginButton
        isLoadingUserData={isLoadingUserData}
        isLogin={isLogin}
      >
        <CompanyDetailContainer company={company} isLogin={isLogin} />
      </LayoutContainer>
    </>
  );
};

export const getServerSideProps = async ({
  query,
  req,
  resolvedUrl,
}: GetServerSidePropsContext) => {
  const url = getCanonicalPageUrl({ resolvedUrl });

  try {
    const { companyId } = query;
    const parsedCompanyId = parseInt(String(companyId));

    // '-'や'wp-admin.php'などの場合にnotFoundを返す
    if (Number.isNaN(parsedCompanyId)) {
      return {
        props: {
          notFound: true,
        },
      };
    }

    const { data: company } = await runWithSentry(
      createSsrClient(req).query<FetchCompanyDetailQuery | undefined>({
        errorPolicy: 'none',
        query: FetchCompanyDetailDocument,
        variables: {
          id: parsedCompanyId,
        },
      })
    );

    const ogpImage = company?.company?.images?.[0]?.url ?? null;
    const companyName = company?.company?.name ?? null;

    return {
      props: {
        url,
        companyName,
        ogpImage,
        company,
      },
    };
  } catch {
    return {
      props: {
        notFound: true,
      },
    };
  }
};

type Props = InferGetServerSidePropsType<typeof getServerSideProps> & {
  isLogin: boolean;
  isLoadingUserData: boolean;
};

export default CompaniesShowPage;
